import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DatePickerInput } from "@/components/ui/date-picker";
import {
  FormikErrorMessage,
  useFormikFieldError,
} from "@/components/FormikErrorMessage";
import { cn } from "@/lib/utils";
import type { FormikProps } from "formik";
import { getIn } from "formik";
import type { SilaeVariable } from "@/types/silae";
import type { SilaeFormData } from "@/types/silae";
import { SilaeDatePicker } from "./SilaeDatePicker";
import { useTranslation } from "react-i18next";

/**
 * Interface pour le composant VariableInput
 */
export interface VariableInputProps {
  formik: FormikProps<SilaeFormData>;
  fieldName: string;
  variableType: string | undefined;
  selectedVariable?: SilaeVariable | null;
  placeholder?: string;
  unit?: string | undefined;
  label?: string;
}

/**
 * Composant pour rendre l'input approprié selon le type de variable
 */
export const VariableInput: React.FC<VariableInputProps> = ({
  formik,
  fieldName,
  variableType,
  selectedVariable,
  placeholder = "Entrer la valeur à comparer",
  unit,
  label,
}) => {
  const { errorClass } = useFormikFieldError(formik, fieldName);
  const value = getIn(formik.values, fieldName) || "";
  const { t } = useTranslation();
  switch (variableType) {
    case "boolean":
      return (
        <div className="space-y-2">
          <div className="flex items-center gap-3 p-3 border rounded-md">
            <Switch
              checked={value === "true"}
              onCheckedChange={(checked) => {
                formik.setFieldValue(fieldName, checked ? "true" : "false");
                // Marquer comme touché pour déclencher la validation en temps réel
                formik.setFieldTouched(fieldName, true);
              }}
            />
            <Label className="text-sm">
              {value === "true" ? "Vrai" : "Faux"}
            </Label>
          </div>
          <FormikErrorMessage
            formik={formik as unknown as FormikProps<Record<string, unknown>>}
            name={fieldName}
          />
        </div>
      );

    case "int":
      return (
        <div className="space-y-2">
          <Input
            type="number"
            step="1"
            pattern="[0-9]*"
            value={value || ""}
            onChange={(e) => {
              const val = e.target.value;
              if (val === "" || /^-?\d+$/.test(val)) {
                formik.setFieldValue(fieldName, val);
                // Marquer comme touché pour déclencher la validation en temps réel
                formik.setFieldTouched(fieldName, true);
              }
            }}
            onBlur={() => {
              formik.setFieldTouched(fieldName, true);
              formik.handleBlur(fieldName);
            }}
            placeholder="Ex: 25, 100, -5"
            className={cn("h-9", errorClass)}
            name={fieldName}
          />
          {unit && <span className="text-sm text-gray-500">{unit}</span>}
          <FormikErrorMessage
            formik={formik as unknown as FormikProps<Record<string, unknown>>}
            name={fieldName}
          />
        </div>
      );

    case "double":
      return (
        <div className="space-y-2">
          <Input
            type="number"
            step="0.01"
            value={value || ""}
            onChange={(e) => {
              const val = e.target.value;
              if (val === "" || /^-?\d*\.?\d*$/.test(val)) {
                formik.setFieldValue(fieldName, val);
                // Marquer comme touché pour déclencher la validation en temps réel
                formik.setFieldTouched(fieldName, true);
              }
            }}
            onBlur={() => {
              formik.setFieldTouched(fieldName, true);
              formik.handleBlur(fieldName);
            }}
            placeholder="Ex: 25.50, 1000.00, -5.25"
            className={cn("h-9", errorClass)}
            name={fieldName}
          />
          <FormikErrorMessage
            formik={formik as unknown as FormikProps<Record<string, unknown>>}
            name={fieldName}
          />
        </div>
      );

    case "date":
      return (
        <div className="space-y-2">
          <DatePickerInput
            value={value || ""}
            onChange={(newValue) => {
              formik.setFieldValue(fieldName, newValue);
              // Marquer le champ comme touché immédiatement après la sélection
              formik.setFieldTouched(fieldName, true);
            }}
            onBlur={() => formik.setFieldTouched(fieldName, true)}
            placeholder="Sélectionner une date"
            hasError={
              !!(
                getIn(formik.errors, fieldName) &&
                getIn(formik.touched, fieldName)
              )
            }
            className="h-9"
          />
          <FormikErrorMessage
            formik={formik as unknown as FormikProps<Record<string, unknown>>}
            name={fieldName}
          />
        </div>
      );

    case "dynamicDate":
      return (
        <div className="space-y-2">
          <SilaeDatePicker
            value={value || ""}
            onChange={(newValue) => {
              formik.setFieldValue(fieldName, newValue);
              // Marquer le champ comme touché immédiatement après la sélection
              formik.setFieldTouched(fieldName, true);
            }}
            placeholder="Ex: Date(1,1,bul.annee)"
            label={label || "date"}
          />
          <FormikErrorMessage
            formik={formik as unknown as FormikProps<Record<string, unknown>>}
            name={fieldName}
          />
        </div>
      );
    case "selection":
      if (selectedVariable?.options) {
        return (
          <div className="space-y-2">
            <Select
              value={value || ""}
              onValueChange={(newValue) =>
                formik.setFieldValue(fieldName, newValue)
              }
            >
              <SelectTrigger className={cn("h-9", errorClass)}>
                <SelectValue
                  placeholder={`${t("variableSelector.placeholder")}`}
                />
              </SelectTrigger>
              <SelectContent>
                {selectedVariable.options.map(
                  (option: { label: string; code: string }) => (
                    <SelectItem key={option.code} value={option.code}>
                      {option.label} ({option.code})
                    </SelectItem>
                  ),
                )}
              </SelectContent>
            </Select>
            <FormikErrorMessage
              formik={formik as unknown as FormikProps<Record<string, unknown>>}
              name={fieldName}
            />
          </div>
        );
      }
      // Fallback to text input if no options
      return (
        <div className="space-y-2">
          <Input
            value={value || ""}
            onChange={(e) => {
              formik.setFieldValue(fieldName, e.target.value);
              // Marquer comme touché pour déclencher la validation en temps réel
              formik.setFieldTouched(fieldName, true);
            }}
            onBlur={() => {
              formik.setFieldTouched(fieldName, true);
              formik.handleBlur(fieldName);
            }}
            placeholder={placeholder}
            className={cn("h-9", errorClass)}
            name={fieldName}
          />
          <FormikErrorMessage
            formik={formik as unknown as FormikProps<Record<string, unknown>>}
            name={fieldName}
          />
        </div>
      );

    default:
      return (
        <div className="space-y-2">
          <Input
            value={value || ""}
            onChange={(e) => {
              formik.setFieldValue(fieldName, e.target.value);
              // Marquer comme touché pour déclencher la validation en temps réel
              formik.setFieldTouched(fieldName, true);
            }}
            onBlur={() => {
              formik.setFieldTouched(fieldName, true);
              formik.handleBlur(fieldName);
            }}
            placeholder={placeholder}
            className={cn("h-9", errorClass)}
            name={fieldName}
          />
          <FormikErrorMessage
            formik={formik as unknown as FormikProps<Record<string, unknown>>}
            name={fieldName}
          />
        </div>
      );
  }
};
